// Legacy device-local diagnostic only. This counter cannot establish first-ever
// account activation or habit cohorts: other devices and historical claims can
// affect it. MOR-1977 replaces it with authoritative indexed trade history.

const KEY_PREFIX = 'morfi.analytics.trade-count.';

/**
 * Record that `userId` completed a trade and return its 1-based ordinal —
 * `1` for the first observation in this browser.
 *
 * Returns `undefined` when the ordinal cannot be trusted (no browser storage,
 * unknown user, storage unavailable in private mode). Callers omit the
 * property entirely in that case rather than guessing a number, so a missing
 * value never masquerades as a first trade.
 */
export function recordTradeAndGetOrdinal(userId: string | null): number | undefined {
  if (typeof window === 'undefined' || userId === null || userId === '') return undefined;
  const key = `${KEY_PREFIX}${userId}`;
  try {
    const stored = window.localStorage.getItem(key);
    const previous = stored === null ? 0 : Number.parseInt(stored, 10);
    const next = Number.isInteger(previous) && previous > 0 ? previous + 1 : 1;
    window.localStorage.setItem(key, String(next));
    return next;
  } catch {
    // SILENT-CATCH: storage blocked (Safari private mode, quota exceeded).
    // Reporting would fire on every trade for an affected browser, and the
    // caller already degrades safely by omitting `userTradeNumber` — a
    // missing ordinal is never read as a first trade.
    return undefined;
  }
}

/** The trade-lifecycle properties to merge into a `trade_success` payload. */
export function tradeLifecycleProperties(userId: string | null): { userTradeNumber?: number } {
  const ordinal = recordTradeAndGetOrdinal(userId);
  return ordinal === undefined ? {} : { userTradeNumber: ordinal };
}
